/*
 * 1-Minute Maths
 * Copyright © 2021 - 2022 White Rose Maths
 * NumberLine100.js - Number Line for count in 100s
*/

import {
  StyleSheet,
  View,
  Text,
} from 'react-native';
import { RFPercentage } from 'react-native-responsive-fontsize';
import { NumberLineProps } from './SequenceProps';
import { Format } from '../../types';


import type { JSX } from 'react';


const UPPER_RANGE = 200;

const NumberLine100 = ({answer, format}: NumberLineProps ): JSX.Element => {

  let numbers= [];
  switch (format) {
    case Format.AdditiveSequence1: {
      numbers = [answer, answer + 100, answer + 200];
      break;
    }
    case Format.AdditiveSequence2:
    case Format.AdditiveSequence3:
    case Format.AdditiveSequence4: {
      numbers = [answer - 100, answer, answer + 100];
      break;
    }

    case Format.AdditiveSequence5: {
      numbers = [answer - 200, answer - 100, answer];
      break;
    }

    default: {
      throw new Error(`Format: ${format}, not used by sequence diagram`);
    }
  }

  const baseNotchLine: JSX.Element[] = [];
  const topNotchLine: JSX.Element[] = [];
  const numberLine: JSX.Element[] = [];

  // The order of components added to notchLine is important.
  // Add the base notches
  for (let i = 0; i < UPPER_RANGE; i+=10) {
    if (i === UPPER_RANGE -10) {
      baseNotchLine.push(<View style={[styles.thickerBaseline, styles.thickerRightLine]} key={'baseNotch' + i} />);
    }
    else if (i === UPPER_RANGE - 20) {
      baseNotchLine.push(<View style={[styles.thickBaseline, styles.thickLeftLine, styles.thickRightLine]} key={'baseNotch' + i} />);
    }
    else if (i % 100 === 0) {
      baseNotchLine.push(<View style={[styles.thickerBaseline, styles.thickerLeftLine]} key={'baseNotch' + i} />);
    }
    else {
      baseNotchLine.push(<View style={[styles.thickBaseline, styles.thickLeftLine]} key={'baseNotch' + i} />);
    }
  }
    
  // Add the top notches
  for (let i = 0; i < UPPER_RANGE; i+=10) {
    if (i === UPPER_RANGE - 10) {
      topNotchLine.push(<View style={[styles.thickerTopline, styles.thickerRightLine]} key={'topNotch' + i} />);
    }
    else if (i === UPPER_RANGE - 20) {
      topNotchLine.push(<View style={[styles.thickTopline, styles.thickLeftLine, styles.thickRightLine]} key={'topNotch' + i} />);
    }
    else if (i % 100 === 0) {
      topNotchLine.push(<View style={[styles.thickerTopline, styles.thickerLeftLine]} key={'topNotch' + i} />);
    }
    else {
      topNotchLine.push(<View style={[styles.thickTopline, styles.thickLeftLine]} key={'topNotch' + i} />);
    }
  }

  // Add the numbers to the line
  numbers.forEach((number, index) => {
    const text = number === answer ? '?' : number.toString();
    switch(index){
      case 0:
      {
        numberLine.push(<Text style={styles.numberLineLeft} key={'numText' + index}>{text}</Text>);
        break;
      }
      case 1:
      {
        numberLine.push(<Text style={styles.numberLineMiddle} key={'numText' + index}>{text}</Text>);
        break;
      }
      case 2:
      {
        numberLine.push(<Text style={styles.numberLineRight} key={'numText' + index}>{text}</Text>);
        break;
      }
    }
  });

  return (
    <View style={styles.sequence} key="sequenceDiagram">
      <View style={styles.notchLine} key="baseNotchLine">
        {baseNotchLine}
      </View>
      <View style={styles.notchLine} key="topNotchLine">
        {topNotchLine}
      </View>
      <View style={styles.numberLine} key="numberLine">
        {numberLine}
      </View>
    </View>
  );
};

export default NumberLine100;

const styles = StyleSheet.create({

  sequence: {
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    alignSelf: 'center',
    marginBottom: 20,
    display: 'flex',
    height: 140,
    width: '100%'
  },

  notchLine: {
    flex: 1,
    justifyContent: 'space-evenly',
    flexDirection: 'row',
    width: '100%',
    height: 40
  },

  numberLine: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    alignContent: 'center',
    alignSelf: 'center',
    textAlignVertical: 'center',
    textAlign: 'center',
    width: '100%',
    height: 60,
    position: 'absolute',
    bottom: -40

  },

  thickerLeftLine: {
    borderStyle: 'solid',
    borderLeftWidth: 3,
    borderLeftColor: '#000',
  },

  thickerRightLine: {
    borderStyle: 'solid',
    borderRightWidth: 3,
    borderRightColor: '#000',
  },

  thickLeftLine: {
    borderStyle: 'solid',
    borderLeftWidth: 2,
    borderLeftColor: '#000',
  },

  thickRightLine: {
    borderStyle: 'solid',
    borderRightWidth: 2,
    borderRightColor: '#000',
  },

  thickBaseline: {
    height: 20,
    borderStyle: 'solid',
    borderBottomWidth: 2,
    borderBottomColor: '#000',
    alignSelf: 'flex-end',
    flex: 1
  },

  thickerBaseline: {
    height: 40,
    borderStyle: 'solid',
    borderBottomWidth: 2,
    borderBottomColor: '#000',
    flex: 1,
    alignSelf: 'flex-end'
  },

  thickTopline: {
    height: 20,
    borderStyle: 'solid',
    borderTopWidth: 2,
    borderTopColor: '#000',
    alignSelf: 'flex-start',
    flex: 1
  },

  thickerTopline: {
    height: 40,
    borderStyle: 'solid',
    borderTopWidth: 2,
    borderTopColor: '#000',
    alignSelf: 'flex-start',
    flex: 1
  },

  numberLineLeft: {
    left: -RFPercentage(1),
    fontSize: RFPercentage(3),
    lineHeight: 60,
    position: 'relative'
  },

  numberLineMiddle: {
    fontSize: RFPercentage(3),
    lineHeight: 60
  },

  numberLineRight: {
    right: -RFPercentage(1),
    fontSize: RFPercentage(3),
    lineHeight: 60,
    position: 'relative'
  },


});